import { useEffect, useRef, useState } from "react"
import { PlayIcon } from "@heroicons/react/20/solid"
import H2 from "components/atoms/H2"
import useTranslations from "hooks/useTranslations"
import { OverviewVideoCoverImage } from "./images"

const VIMEO_ORIGIN = "https://player.vimeo.com"

/**
 * Suvi's overview of the service, between the testimonials and the
 * integrations section, where Ellinora asked for it.
 *
 * Heading and lead above the player, per Kaisa: a cover with no words around
 * it did not say that the person speaking is our founder and social media
 * researcher, or why that is worth two minutes. Ellinora wrote both lines and
 * the cover frame of Suvi mid-sentence — earlier the cover was the video's
 * general title card, which repeated photography already on the page.
 *
 * Click-to-play like VideoIntro, but preloaded: the player iframe sits under
 * the cover from the start (`loading="lazy"`, so it is fetched as the visitor
 * scrolls towards it, not on the initial load), and the click only lifts the
 * cover and tells the already-loaded player to play. A click that beats the
 * player's load is held until Vimeo reports `ready`.
 *
 * `dnt=1` stops the player setting cookies or sending analytics, the Vimeo
 * counterpart of `youtube-nocookie.com`. The video is "Embed only" on Vimeo,
 * which needs no `h=` hash in the URL (only "Unlisted" does).
 */
export default function UkOverviewVideo() {
  const {
    ukSchoolsOverviewVideoId,
    ukSchoolsOverviewVideoTitle,
    ukSchoolsOverviewVideoHeading,
    ukSchoolsOverviewVideoLead,
  } = useTranslations()
  const [playing, setPlaying] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const ready = useRef(false)
  const playPending = useRef(false)

  const sendPlay = () =>
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ method: "play" }),
      VIMEO_ORIGIN,
    )

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== VIMEO_ORIGIN) return
      if (e.source !== iframeRef.current?.contentWindow) return
      let data
      try {
        data = typeof e.data === "string" ? JSON.parse(e.data) : e.data
      } catch {
        return
      }
      if (data?.event !== "ready") return
      ready.current = true
      if (playPending.current) {
        playPending.current = false
        sendPlay()
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [])

  const play = () => {
    setPlaying(true)
    if (ready.current) sendPlay()
    else playPending.current = true
  }

  if (!ukSchoolsOverviewVideoId) return null

  return (
    <div className="bg-kkm-green py-16 lg:py-24">
      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* The lead is one sentence, so it reads better at a narrower measure
            than the player it introduces. */}
        <div className="mb-8 max-w-2xl lg:mb-10">
          <H2
            size="lg"
            className="m-0 whitespace-pre-line text-left font-extrabold tracking-tight"
            theme="light"
          >
            {ukSchoolsOverviewVideoHeading}
          </H2>
          <p className="m-0 mt-4 text-base leading-relaxed text-zinc-800 lg:text-lg">
            {ukSchoolsOverviewVideoLead}
          </p>
        </div>
        {/* No background or ring on the frame: the rounded clip anti-aliases
            its edge, and anything dark underneath (a fill, a ring, the
            player's black iframe) shows through as a hairline around the
            cover. The shadow alone separates it from the mint ground. */}
        <div className="overflow-hidden rounded-2xl shadow-xl">
          <div className="relative h-0 pb-fluid-video">
            <iframe
              ref={iframeRef}
              // Invisible, not unmounted, until playback: it keeps loading
              // under the cover, but its black ground can't bleed at the edge.
              className={`absolute inset-0 h-full w-full ${
                playing ? "" : "opacity-0"
              }`}
              src={`${VIMEO_ORIGIN}/video/${ukSchoolsOverviewVideoId}?dnt=1&title=0&byline=0&portrait=0`}
              title={ukSchoolsOverviewVideoTitle}
              loading="lazy"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              allowFullScreen
            />
            {!playing && (
              <button
                type="button"
                onClick={play}
                aria-label={`Play video: ${ukSchoolsOverviewVideoTitle}`}
                className="group absolute inset-0 h-full w-full cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-zinc-900"
              >
                <OverviewVideoCoverImage className="h-full w-full" />
                {/* Bottom-right, not centred: the title card's lettering runs
                    through the middle, and a centred button sits on it. */}
                <span
                  aria-hidden="true"
                  className="absolute inset-0 flex items-end justify-end bg-zinc-900/10 p-4 transition-colors group-hover:bg-zinc-900/30 sm:p-6 lg:p-8"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform duration-150 ease-out group-hover:scale-105 group-active:scale-[0.96] sm:h-20 sm:w-20">
                    <PlayIcon className="ml-1 h-8 w-8 text-zinc-900 sm:h-10 sm:w-10" />
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
